import {
	Box, IconButton, makeStyles,
	Paper, Table as MUITable,
	TableBody,
	TableContainer,
	TableFooter,
	Tooltip
} from "@material-ui/core";
import { HighlightOff, Lock, LockOpen } from "@material-ui/icons";
import Skeleton from '@material-ui/lab/Skeleton';
import _ from 'lodash';
import moment from "moment";
import PropTypes from "prop-types";
import React from "react";
import { permissionForRoles, permissionNames } from "../../../../constants/permissions";
import { validAscents } from "../../../../constants/propTypesValidation";
import theme from "../../../../styles/theme";
import { filterObjectByFields } from "../../../../utils/other";
import { styledBy } from "../../../../utils/styles";
import ExportCsvButton from "../../Button/ExportCsvButton";
import TextBox from "../../Inputs/TextBox";
import CircularProgress from "../../ProgressBar/CircularProgress";
import Text from "../../Text";
import TableData from "./TableData";
import TableHead from "./TableHead";
import TablePagination from "./TablePagination";
import TableRow from "./TableRow";


const useStyle = makeStyles((theme) => ({
	root: {
		width: "100%",
		background: `linear-gradient(-45deg, ${theme.palette.ascents.primary},${theme.palette.ascents.orange})`,
		padding: "1px",
		borderRadius: "8px",
	},
	paper: {
		width: "100%",
		backgroundColor: theme.palette.background.default,
		borderRadius: "8px",
		overflow: "hidden"
		// marginBottom: theme.spacing(2),
	},
	topHeader: {
		width: "100%",
		display: "flex",
		background: styledBy("headerBgAscent", theme.palette.ascents),
		padding: theme.spacing(3),
		alignItems: "center",
	},
	searchBox: {
		color: theme.palette.ascents.systemWash,
		marginRight: '1rem'
	},
	skeleton: {
		height: "100%",
	},
	footer: {
		"& .MuiTablePagination-actions": {
			"& .MuiSvgIcon-root": {
				fontSize: "1rem",
			}
		},
		"& .MuiTableCell-root": {
			borderBottom: "none"
		},
		"& .MuiSvgIcon-root": {
			color: theme.palette.ascents.textDark,
		},
		"& p": {
			color: theme.palette.ascents.textLightDark,

		}
	}
}));

const Table = ({
	align,
	TopHeaderComponent,
	ActiveComponent,
	InActiveComponent,
	handleSwitchToggle,
	headCells = {},
	tableData = [],
	onClickAction,
	crud = {
		...permissionForRoles,
		[permissionNames.CAN_DELETE]: 0,
	},
	emptyRowMessage = "No results found",
	elevation = 0,
	hideSelect = true,
	hideCrudButtons = false,
	hideActionCol = false,
	headerBgAscent = "systemMetalLight",
	// pages = { page: 0, handlePageChange: () => { } },
	// rowsPerPage = { rowsPerPage: 5, handleRowsPerPage: () => { } },
	handleCheck,
	isSelected,
	onEditData,
	onReadData,
	onDeleteData,
	value,
	onSearchInput,
	searchBoxIsEnabled = true,
	hideFooter = false,
	theadAscent,
	CustomAction,
	CustomFooter,
	hasFormField,
	formConstant,
	showDownloadButton = false,
	filerByDateRange = {
		enable: false,
		columnName: 'createdAt'
	},
	tableName = '',
	isLoading = false,
	...props
}) => {
	const classes = useStyle({
		headerBgAscent,
	});

	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(15);
	const [order, setOrder] = React.useState('asc');
	const [orderBy, setOrderBy] = React.useState();

	const [finalTableData, setFinalTableData] = React.useState([])
	const [query, setQuery] = React.useState(!!value || '')
	const [matchExactQuery, setMatchExactQuery] = React.useState(false);
	const [dateRange, setDateRange] = React.useState({
		start: '',
		end: ''
	})

	const handleMatchExactQueryToggle = () => {
		setMatchExactQuery(!matchExactQuery)
	}

	const clearAllSearchInput = () => {
		setQuery('')
		setDateRange({
			start: '',
			end: ''
		})
		setFinalTableData(tableData)
	}

	const handleStartDateInput = (e) => {
		const input = e.target.value
		setDateRange({
			...dateRange,
			start: input
		})
		console.log(input)
	}

	const handleEndDateInput = (e) => {
		const input = e.target.value
		setDateRange({
			...dateRange,
			end: input
		})
		console.log(input)
	}

	const checkDateRange = (eachRow = {}, returnValue = false) => {
		if (filerByDateRange.columnName && eachRow[filerByDateRange.columnName] && moment(eachRow[filerByDateRange.columnName])) {
			const date = moment(eachRow[filerByDateRange.columnName]).format('YYYY-MM-DD')
			// both
			if (!!dateRange.start && !!dateRange.end) {
				returnValue = moment(date).isBetween(dateRange.start, dateRange.end, null, '[]')
			}
			// search after start date
			else if (!!dateRange.start && dateRange.end === '') {
				returnValue = moment(date).isSameOrAfter(dateRange.start, null,)
			}
			// search before end date
			else if (!!dateRange.end && dateRange.start === '') {
				returnValue = moment(date).isSameOrBefore(dateRange.end)
			}
		}
		return returnValue
	}

	// since this calculation will take time to finish, i made it asynchronous
	const getFilteredRows = (input) => {
		return new Promise((resolve, reject) => {
			let updatedTableData = []

			updatedTableData = tableData.filter(eachRow => {
				let returnValue = false;

				// filtering out column names which is not visible to the user
				let allowdColumns = []
				Object.keys(headCells).map(eachKey => {
					if (!headCells[eachKey].hidden) {
						allowdColumns.push(eachKey)
					}
				})

				// filtering out only allowed fields
				eachRow = filterObjectByFields(eachRow, allowdColumns)

				// stores the list of all value in the row object
				let filteredValues = Object.values(eachRow);

				// if search query is given then filter rows by the query then check for date range
				if (!!input) {
					if (matchExactQuery) {
						returnValue = _.includes(filteredValues, input);
					} else {
						returnValue = _.some(eachRow, _.unary(_.partialRight(_.includes, input)));
					}
					// if it matches with query check the date range otherwise no need
					if (returnValue) {
						returnValue = checkDateRange(eachRow, returnValue)
					}
				}
				else {
					returnValue = checkDateRange(eachRow, returnValue)
				}
				return returnValue
			})
			resolve(updatedTableData)
		})
	}

	const handleOnSearchInput = (e) => {
		const input = e.target.value
		setQuery(input)
		if ((input.length > 0) && (!!dateRange.start || !!dateRange.end)) {
			getFilteredRows(input).then(updatedTableData => {
				if (updatedTableData.length > 0) {
					setFinalTableData(updatedTableData)
				}
			})
		}
		// if user deletes everything or search query is empty and date range is also not given then set table data to the prop passed
		else if (input.length === 0) {
			if (!!dateRange.start || !!dateRange.end) {
				getFilteredRows(input).then(updatedTableData => {
					if (updatedTableData.length > 0) {
						setFinalTableData(updatedTableData)
					}
				})
			} else {
				setFinalTableData(tableData);
			}
		}
		// otherwise filter accordingly
		else {
			getFilteredRows(input).then(updatedTableData => {
				if (updatedTableData.length > 0) {
					setFinalTableData(updatedTableData)
				}
			})
		}
	}

	const handlePageChange = (event, newPage) => {
		setPage(newPage);
	};
	const handleRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};


	const handleRequestSort = (event, property) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	React.useEffect(() => {
		setFinalTableData(tableData)
	}, [tableData])

	React.useEffect(() => {
		if (!!query) {
			getFilteredRows(query).then(updatedTableData => {
				if (matchExactQuery) {
					setFinalTableData(updatedTableData)
				}
				else {
					if ((updatedTableData.length > 0) || !!query || !!dateRange.start || !!dateRange.end) {
						setFinalTableData(updatedTableData)
					}
				}
			})
		} else {
			getFilteredRows(query).then(updatedTableData => {
				if (matchExactQuery) {
					if (!!dateRange.start || !!dateRange.end) {
						setFinalTableData(updatedTableData)
					}
				}
				else {
					if ((updatedTableData.length > 0) || !!query || !!dateRange.start || !!dateRange.end) {
						setFinalTableData(updatedTableData)
					}
				}
			})
		}
	}, [matchExactQuery, dateRange, tableData])

	return (
		<Box className={classes.root}>
			<Paper elevation={elevation} className={classes.paper}>
				<Box className={classes.topHeader} style={{ display: 'flex', justifyContent: 'space-between' }}>
					<Text fontSize={18} fontWeight={600}>{tableName}</Text>
					<Box>
						{isLoading ? <CircularProgress size={20} /> : null}
					</Box>
				</Box>

				{
					searchBoxIsEnabled && (
						<Box className={classes.topHeader}>
							{TopHeaderComponent ? (
								TopHeaderComponent
							) : (
								<>
									<TextBox
										labelClassName={classes.searchBox}
										inputClassName={classes.searchBox}
										value={query}
										onChange={handleOnSearchInput}
										label={"Search..."}
									/>
									{
										!!filerByDateRange.enable &&
										<>
											<TextBox
												labelClassName={classes.searchBox}
												inputClassName={classes.searchBox}
												value={dateRange.start}
												onChange={handleStartDateInput}
												label={"Start Date"}
												type='date'
												labelProps={{
													shrink: true
												}}
											/>
											<TextBox
												labelClassName={classes.searchBox}
												inputClassName={classes.searchBox}
												value={dateRange.end}
												onChange={handleEndDateInput}
												label={"End Date"}
												type='date'
												labelProps={{
													shrink: true
												}}
											/>
										</>
									}
									<Tooltip title={"Clear All"}>
										<IconButton aria-label="Match whole word" onClick={clearAllSearchInput}>
											<HighlightOff style={{ color: theme.palette.primary.main }} />
										</IconButton>
									</Tooltip>
									<Tooltip title={"Match whole word"}>
										<IconButton aria-label="Match whole word" onClick={handleMatchExactQueryToggle}>
											{
												matchExactQuery ? <Lock style={{ color: theme.palette.primary.main }} /> : <LockOpen style={{ color: theme.palette.primary.main }} />
											}
										</IconButton>
									</Tooltip>
									{
										!!showDownloadButton &&
										<ExportCsvButton headCells={headCells} data={finalTableData} />
									}
								</>
							)}
						</Box>
					)
				}
				<TableContainer>
					<MUITable className={classes.tableRoot}>
						<TableHead
							align={align}
							hideSelect={hideSelect}
							hideActions={hideActionCol || Object.keys(crud).length === 0}
							cols={headCells}
							ascent={theadAscent}
						/>

						<TableBody

						>
							{
								finalTableData.length > 0 ? (
									<TableData
										align={align}
										ActiveComponent={ActiveComponent}
										InActiveComponent={InActiveComponent}
										onReadData={onReadData}
										onEditData={onEditData}
										handleCheck={handleCheck}
										isSelected={isSelected}
										onDeleteData={onDeleteData}
										crud={crud}
										hideCrudButtons={hideCrudButtons}
										hideSelect={hideSelect}
										hideActionCol={hideActionCol || Object.keys(crud).length === 0}
										page={page}
										rowsPerPage={rowsPerPage}
										headCells={headCells}
										tableData={finalTableData}
										CustomAction={CustomAction}
										hasFormField={hasFormField}
										formConstant={formConstant}
										handleSwitchToggle={handleSwitchToggle}
										onClickAction={onClickAction}
									/>
								) : (
									<Skeleton animation="wave" width={"100%"} variant="rect" className={classes.skeleton} />
								)
							}

							{/* <TableRow style={{ height: emptyRows * 40 }}></TableRow> */}
						</TableBody>

						{
							!hideFooter && (
								<TableFooter className={classes.footer}>
									{
										CustomFooter ? CustomFooter : (
											<TableRow>
												<TablePagination
													rowsPerPageOptions={[5]}
													colSpan={Object.keys(headCells).length + 2}
													count={finalTableData.length}
													rowsPerPage={rowsPerPage}
													page={page}
													onChangePage={handlePageChange}
													onChangeRowsPerPage={handleRowsPerPage}
												/>
											</TableRow>
										)
									}
								</TableFooter>
							)
						}
					</MUITable>
				</TableContainer>
			</Paper>
		</Box>
	);
};

Table.propTypes = {
	headCells: PropTypes.objectOf(PropTypes.object),
	tableData: PropTypes.arrayOf(PropTypes.object),
	headerBgAscent: validAscents,
	onSearchInput: PropTypes.func,
	hideSelect: PropTypes.bool,
	hideActionCol: PropTypes.bool,
	crud: PropTypes.object,
	TopHeaderComponent: PropTypes.oneOfType([
		PropTypes.element,
		PropTypes.elementType,
	]),
	pages: PropTypes.object,
	rowsPerPage: PropTypes.object,
};

export default Table;
